import { LabelType, Options } from '@angular-slider/ngx-slider';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Province, RecipientCriteria, Region, SmsAdv, SmsAdvCriteriaFields, ZipCodes } from '@models/smsadv/smsadv';
import { AtlasToastService } from '@services/atlas-toast.service';
import { SmsAdvCreateTabHandlerService } from '@services/sms-adv-create-tab-handler.service';
import { SmsAdvService } from '@services/smsadv.service';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TabSmsAdv } from '../sms-adv-create.component';

@Component({
  selector: 'wsb-sms-adv-select-recipients',
  templateUrl: './sms-adv-sel-recipients.component.html',
  styleUrls: ['./sms-adv-sel-recipients.component.scss'],
  animations: [
    trigger('toggleHeight', [
      state(
        'hide',
        style({
          height: '0px',
          opacity: '0',
          overflow: 'hidden'
        })
      ),
      state(
        'show',
        style({
          height: '*',
          opacity: '1'
        })
      ),
      transition('hide => show', animate('200ms ease-in')),
      transition('show => hide', animate('200ms ease-out'))
    ])
  ],
  standalone: false
})
export class SmsAdvSelRecipientsComponent implements OnInit, OnDestroy {
  @Input() smsAdvCampaignToSend: SmsAdv;
  @Output() enableProceedBtn: EventEmitter<boolean> = new EventEmitter<boolean>();

  selectionForm: UntypedFormGroup;
  maxValueSliderDemograph = 100;
  isShowAreaGeographic = true;
  radioAreaGeo = true;
  recipientEstimate;
  isShowLimitRecipient = false;
  isShowCountRecipient = false;
  isShowWarnMsgProvinces = false;
  isShowWarnMsgZipCodes = false;
  listSearchCriteria: RecipientCriteria;

  selectRegions;
  selectProvinces;
  selectZipCodes;

  loading = false;

  tabSubscription: Subscription;

  readonly GENDER_ALL = 'ALL';
  readonly GENDER_MALE = 'M';
  readonly GENDER_FEMALE = 'F';
  readonly AGE_RANGES_DEFAULT_VALUE = '18-100';

  minLengthStartSearch = 2;
  rangeSliderDemoGraph: Options = {
    floor: 18,
    ceil: 100,
    step: 1,
    showTicks: true,
    translate: (value: number, label: LabelType): string => {
      if (value === this.rangeSliderDemoGraph.ceil) {
        return value + '+';
      }
      return value + '';
    }
  };

  genderFC = new UntypedFormControl(this.GENDER_ALL);
  ageRangesFC = new UntypedFormControl('18');
  limitRecipientFC = new UntypedFormControl('0');

  constructor(
    private formBuilder: UntypedFormBuilder,
    private smsAdvService: SmsAdvService,
    private atlasToast: AtlasToastService,
    private tabHandlerService: SmsAdvCreateTabHandlerService
  ) {}

  ngOnInit(): void {
    this.buildSelectionForm();
    this.verifyToInitSelectionForm();
    this.callSearchCriteria();
    this.genderFCValueChange();
    this.ageRangesFCValueChange();
    this.recipientCriteriaFormValueChange();
    this.limitRecipientFCValueChange();
    this.handlerCloseTab();
  }

  getRecipientCriteriaForm() {
    return this.selectionForm.get('recipientCriteria');
  }

  ngOnDestroy(): void {
    this.tabSubscription.unsubscribe();
  }

  changeRegions(regs: Region[]) {
    if (regs) {
      this.setListProvinceByRegions(regs);
      this.setListZipCodesByRegions(regs);
      this.verifyWarnMsgProvinceByRegions(regs);
      this.verifyWarnMsgZipCodesByRegions(regs);
    }
  }

  changeProvinces(provs: Province[]) {
    if (provs) {
      this.setListZipCodesByProvinces(provs);
      this.verifyWarnMsgZipCodesByProvinces(provs);
    }
  }

  get provinces() {
    return this.getRecipientCriteriaForm().get(SmsAdvCriteriaFields.FIELDS_PROVINCES);
  }

  get zipCodes() {
    return this.getRecipientCriteriaForm().get(SmsAdvCriteriaFields.FIELD_ZIP_CODES);
  }

  get limitRecipients() {
    return this.selectionForm.get('limitRecipients');
  }

  // private method
  private verifyWarnMsgZipCodesByRegions(regs: Region[]) {
    this.isShowWarnMsgZipCodes = this.isChangedZipCodesValueByRegion(regs);
  }

  private verifyWarnMsgProvinceByRegions(regs: Region[]) {
    this.isShowWarnMsgProvinces = this.isChangedProvincesValueByRegion(regs);
  }

  private verifyWarnMsgZipCodesByProvinces(provs: Province[]) {
    this.isShowWarnMsgZipCodes = this.isChangedZipCodesValueByProvinces(provs);
  }
  // change zipCode by province
  private isChangedZipCodesValueByProvinces(provs: Province[]) {
    const currentValue: ZipCodes[] = this.zipCodes.value;
    return currentValue ? this.valueChangeZipCodesByProvinces(currentValue, provs) : false;
  }
  // change zipCode by province
  private valueChangeZipCodesByProvinces(currentValue: ZipCodes[], provs: Province[]) {
    const provIds = provs.map(p => p.abbreviation);
    const newValue = currentValue.filter(zip => !provIds.includes(zip.province));
    return newValue.length !== currentValue.length ? (this.zipCodes.setValue(newValue), true) : false;
  }
  // change province by region
  private isChangedProvincesValueByRegion(regs: Region[]) {
    const currentValue: Province[] = this.provinces.value;
    return currentValue ? this.valueChangeProvincesByRegions(currentValue, regs) : false;
  }
  // change province by region
  private valueChangeProvincesByRegions(currentValue: Province[], regs: Region[]) {
    const regsIds = regs.map(r => r.region);
    const newValue = currentValue.filter(p => !regsIds.includes(p.region));
    return newValue.length !== currentValue.length ? (this.provinces.setValue(newValue), true) : false;
  }

  // change zipCode by region
  private isChangedZipCodesValueByRegion(regs: Region[]) {
    const currentValue: ZipCodes[] = this.zipCodes.value;
    return currentValue ? this.valueChangeZipCodesByRegion(currentValue, regs) : false;
  }

  // change zipCode by region
  private valueChangeZipCodesByRegion(currentValue: ZipCodes[], regs: Region[]) {
    const regsIds = regs.map(r => r.region);
    const newValue = currentValue.filter(z => !regsIds.includes(z.region));
    return newValue.length !== currentValue.length ? (this.zipCodes.setValue(newValue), true) : false;
  }

  private setListProvinceByRegions(regs: Region[]) {
    const regsId = regs.map(r => r.region);
    // setta la lista
    const list = this.listSearchCriteria.provinces.filter(p => !regsId.includes(p.region));
    this.selectProvinces = list;
  }

  private setListZipCodesByRegions(regs: Region[]) {
    const regsIds = regs.map(r => r.region);
    const list = this.listSearchCriteria.zipCodes.filter(z => !regsIds.includes(z.region));
    this.selectZipCodes = list;
  }

  private setListZipCodesByProvinces(provs: Province[]) {
    const provIds = provs.map(p => p.abbreviation);
    const list = this.listSearchCriteria.zipCodes.filter(z => !provIds.includes(z.province));
    this.selectZipCodes = list;
  }

  private handlerCloseTab() {
    this.tabSubscription = this.tabHandlerService.closeTab$.subscribe(tab => {
      if (tab === TabSmsAdv.TAB_SELECT_RECIPIENT) {
        this.setFormResult();
        return this.tabHandlerService.setTabIsCompleted(TabSmsAdv.TAB_SELECT_RECIPIENT);
      }
    });
  }

  private setFormResult() {
    const obs$ = this.tabHandlerService.valuesForm$(this.selectionForm);
    this.tabHandlerService.saveTabFormData(obs$);
  }

  private limitRecipientFCValueChange() {
    this.limitRecipientFC.valueChanges.subscribe(value => {
      this.isShowLimitRecipient = value > 0 ? true : false;
      if (!this.isShowLimitRecipient) {
        this.limitRecipients.reset();
        this.limitRecipients.setValidators(null);
        this.limitRecipients.updateValueAndValidity();
      } else {
        this.limitRecipients.setValidators(Validators.required);
        this.limitRecipients.updateValueAndValidity();
      }
    });
  }

  private recipientCriteriaFormValueChange() {
    this.getRecipientCriteriaForm()
      .valueChanges.pipe(debounceTime(300))
      .subscribe(value => {
        if (this.isSelectedCriteriaGeograpichs()) {
          this.callApiCountRecipients();
        }
      });
  }

  private ageRangesFCValueChange() {
    this.ageRangesFC.valueChanges.subscribe(value => {
      this.setCriteriaAgesByAgeRangeFC(value);
    });
  }

  private genderFCValueChange() {
    this.genderFC.valueChanges.subscribe(value => {
      this.setCriteriaGendersByGenderFC(value);
    });
  }

  private isSelectedCriteriaGeograpichs(): boolean {
    const area = this.getRecipientCriteriaForm().value;
    if (area.regions && area.regions.length > 0) {
      this.isShowCountRecipient = true;
      return true;
    } else if (area.provinces && area.provinces.length > 0) {
      this.isShowCountRecipient = true;
      return true;
    } else if (area.zipCodes && area.zipCodes.length > 0) {
      this.isShowCountRecipient = true;
      return true;
    }
    this.isShowCountRecipient = false;
    return false;
  }

  private callApiCountRecipients() {
    const requestBody: RecipientCriteria = this.getRecipientCriteriaForm().value;
    this.smsAdvService.countRecipients(requestBody).subscribe(
      success => (this.recipientEstimate = success.countRecipients),
      error => this.atlasToast.error('common_error', null)
    );
  }

  private setCriteriaGendersByGenderFC(value) {
    switch (value) {
      case this.GENDER_ALL:
        this.getRecipientCriteriaForm()
          .get(SmsAdvCriteriaFields.FIELD_GENDERS)
          .setValue([this.GENDER_MALE, this.GENDER_FEMALE]);
        break;
      case this.GENDER_MALE:
        this.getRecipientCriteriaForm()
          .get(SmsAdvCriteriaFields.FIELD_GENDERS)
          .setValue([this.GENDER_MALE]);

        break;
      case this.GENDER_FEMALE:
        this.getRecipientCriteriaForm()
          .get(SmsAdvCriteriaFields.FIELD_GENDERS)
          .setValue([this.GENDER_FEMALE]);
        break;
    }
  }

  private setCriteriaAgesByAgeRangeFC(value) {
    this.getRecipientCriteriaForm()
      .get(SmsAdvCriteriaFields.FIELD_AGE_RANGES)
      .setValue([value[0] + '-' + value[1]]);
  }

  private callSearchCriteria() {
    this.smsAdvService.searchCriteria().subscribe(criterias => {
      this.listSearchCriteria = criterias;
      this.initSelectRegions();
      this.initSelectProvince();
      this.initSelectZipCodes();
      this.initAreaGeographicsForm();
    });
  }

  private initSelectRegions() {
    this.selectRegions = this.listSearchCriteria.regions;
  }

  private initSelectProvince() {
    this.selectProvinces = this.listSearchCriteria.provinces;
  }

  private initSelectZipCodes() {
    this.selectZipCodes = this.listSearchCriteria.zipCodes;
  }

  private buildSelectionForm() {
    this.selectionForm = this.formBuilder.group({
      recipientCriteria: this.formBuilder.group({
        genders: [[this.GENDER_MALE, this.GENDER_FEMALE], [Validators.required]],
        ageRanges: [[this.AGE_RANGES_DEFAULT_VALUE], [Validators.required]],
        regions: [],
        provinces: [],
        zipCodes: []
      }),
      countRecipients: [],
      limitRecipients: []
    });

    this.selectionFormValueChanges();
  }

  private verifyToInitSelectionForm() {
    if (this.smsAdvCampaignToSend) {
      this.selectionForm.patchValue(this.smsAdvCampaignToSend);
      this.initGenderForm(this.smsAdvCampaignToSend.recipientCriteria.genders);
      this.initAgeRangerForm(this.smsAdvCampaignToSend.recipientCriteria.ageRanges);
      this.initLimitRecipientForm(this.smsAdvCampaignToSend.limitRecipients);
    }
  }

  private initAreaGeographicsForm() {
    const regions: Region[] = this.getRecipientCriteriaForm().get(SmsAdvCriteriaFields.FIELD_REGIONS).value;
    const provinces: Province[] = this.getRecipientCriteriaForm().get(SmsAdvCriteriaFields.FIELDS_PROVINCES).value;
    if (regions && regions.length > 0) {
      this.setListProvinceByRegions(regions);
      this.setListZipCodesByRegions(regions);
      if (provinces && provinces.length > 0) {
        const provIds = provinces.map(p => p.abbreviation);
        const list = this.selectZipCodes.filter(z => !provIds.includes(z.province));
        this.selectZipCodes = list;
      }
    } else if (provinces && provinces.length > 0) {
      this.setListZipCodesByProvinces(provinces);
    }
  }

  private initLimitRecipientForm(value) {
    if (value && value > 0) {
      this.isShowLimitRecipient = true;
      this.limitRecipientFC.setValue('1');
    }
  }

  private initGenderForm(genders: string[]) {
    if (genders && genders.length > 1) {
      this.genderFC.setValue(this.GENDER_ALL);
    } else if (genders && genders.length === 1) {
      this.genderFC.setValue(genders[0]);
    }
  }

  private initAgeRangerForm(ageRanges: string[]) {
    if (ageRanges) {
      const age = ageRanges[0];
      if (!age.match(this.AGE_RANGES_DEFAULT_VALUE)) {
        const ageSplit = age.split('-');
        this.ageRangesFC.setValue(ageSplit);
      }
    }
  }

  private selectionFormValueChanges() {
    this.selectionForm.valueChanges.subscribe(() => {
      if (this.selectionForm.valid && this.isSelectedCriteriaGeograpichs()) {
        return this.enableProceedBtn.emit(true);
      }
      this.enableProceedBtn.emit(false);
    });
  }
}
